import React, { useState } from "react";
import {
  ArrowRight, BedDouble, CalendarDays, Coins, Lightbulb, Moon,
  Sparkles, Target, TrendingDown, TrendingUp, Trophy, Users,
} from "lucide-react";
import { suggestOppKpi } from "@/lib/opportunityEngine";

const FLAG = {
  FR: "🇫🇷", GB: "🇬🇧", DE: "🇩🇪", US: "🇺🇸", IN: "🇮🇳", AE: "🇦🇪", JP: "🇯🇵",
};

const actionVerb = (type) => ({
  decline: "Reconquérir",
  growth: "Accélérer",
  air: "Convertir",
  dominant: "Consolider",
  concentration: "Diversifier",
  benchmark_gap: "Accélérer",
  observed: "Développer",
}[type] || "Explorer");

const priorityMeta = (opportunity) => {
  if (opportunity.confidence === "High") return { label: "Prioritaire", className: "bg-rose-50 text-rose-600" };
  if (opportunity.confidence === "Medium") return { label: "Accélérer", className: "bg-violet-50 text-violet-600" };
  return { label: "Consolider", className: "bg-emerald-50 text-emerald-700" };
};

const marketCode = (opportunity) => String(opportunity.marketKey || "").split("-")[0].toUpperCase();
const formatNumber = (value, digits = 1) => Number.isFinite(Number(value))
  ? new Intl.NumberFormat("fr-FR", { maximumFractionDigits: digits }).format(Number(value))
  : "—";

const SummaryKpi = ({ icon: Icon, accent, label, value, unit, sub }) => (
  <article className={`rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm ${accent}`}>
    <div className="flex items-center gap-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-current/10">
        <Icon size={21} />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-lodge-ardoise">{label}</p>
        <p className="mt-0.5 truncate font-heading text-3xl font-bold tracking-tight text-lodge-graphite">
          {value} {unit && <span className="text-xl">{unit}</span>}
        </p>
        <p className="mt-1 text-xs text-slate-400">{sub}</p>
      </div>
    </div>
  </article>
);

const Confidence = ({ value }) => {
  const active = value === "High" ? 4 : value === "Medium" ? 3 : 2;
  const label = value === "High" ? "élevée" : value === "Medium" ? "moyenne" : "faible";
  return (
    <span className="inline-flex items-center gap-1" aria-label={`Confiance ${label}`}>
      {[0, 1, 2, 3].map((index) => (
        <i key={index} className={`h-1.5 w-4 rounded-full ${index < active ? "bg-emerald-500" : "bg-slate-200"}`} />
      ))}
    </span>
  );
};

export default function DestinationDecisionOverview({
  destinationName,
  period,
  tourismKpis,
  snapshotKpis,
  opportunities,
  occupancy,
  immutableDemo,
  onOpenMarket,
  onRunAction,
}) {
  const [showAll, setShowAll] = useState(false);
  const portfolio = opportunities || [];
  const topFive = portfolio.slice(0, 5);
  const visibleOpportunities = showAll ? portfolio : topFive;
  const actionPlan = portfolio.slice(0, 3);
  const roomRows = (occupancy || []).filter((row) => Number.isFinite(Number(row.rooms)));
  const lowOccupancy = roomRows.length
    ? roomRows.reduce((lowest, row) => Number(row.rooms) < Number(lowest.rooms) ? row : lowest)
    : null;
  const averageOccupancy = roomRows.length
    ? roomRows.reduce((sum, row) => sum + Number(row.rooms), 0) / roomRows.length
    : null;
  const occupancyGap = lowOccupancy && averageOccupancy != null
    ? averageOccupancy - Number(lowOccupancy.rooms)
    : null;
  const leadMessage = lowOccupancy
    ? `${lowOccupancy.month} est votre prochain levier de croissance.`
    : portfolio[0]
      ? `${portfolio[0].market} est votre prochaine priorité de marché.`
      : "Vos leviers apparaîtront dès que les données seront disponibles.";

  const scrollToLevers = () => document.getElementById("destination-opportunities")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <section className="space-y-5" data-testid="destination-decision-overview">
      <div className="flex flex-col gap-3 rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50 via-white to-emerald-50 px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-lodge-deep">Vue destination</p>
          <h2 className="mt-1 font-heading text-3xl font-bold tracking-tight text-lodge-graphite sm:text-4xl">{destinationName}</h2>
          <p className="mt-1 text-sm text-lodge-ardoise">Votre destination, vos prochaines décisions.</p>
        </div>
        <div className="inline-flex items-center gap-2 self-start rounded-xl border border-white bg-white/80 px-3 py-2 text-xs font-semibold text-lodge-deep shadow-sm">
          <CalendarDays size={15} /> {period || "Période indisponible"}
        </div>
      </div>

      <div className="flex flex-col gap-4 overflow-hidden rounded-2xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-teal-50 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="destination-essential">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-lodge-deep text-white shadow-sm"><Sparkles size={21} /></span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-lodge-deep">L’essentiel aujourd’hui</p>
            <p className="mt-0.5 font-heading text-xl font-bold text-lodge-graphite">{leadMessage}</p>
            <p className="mt-1 text-sm text-lodge-ardoise">Stimulez la demande et activez vos marchés à fort potentiel.</p>
          </div>
        </div>
        <button type="button" onClick={scrollToLevers} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-lodge-deep px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#2C5540]">
          Voir les leviers <ArrowRight size={16} />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="destination-primary-kpis">
        <SummaryKpi icon={Users} accent="text-blue-600 border-t-4 border-t-blue-400" label="Arrivées visiteurs" value={tourismKpis.arrivals?.value || "—"} unit={tourismKpis.arrivals?.unit} sub="Destination globale" />
        <SummaryKpi icon={Moon} accent="text-violet-600 border-t-4 border-t-violet-400" label="Séjour moyen" value={tourismKpis.averageStay?.value || "—"} unit={tourismKpis.averageStay?.unit} sub="Pondéré par les arrivées" />
        <SummaryKpi icon={Coins} accent="text-amber-600 border-t-4 border-t-amber-400" label="Recettes touristiques" value={tourismKpis.receipts?.value || "—"} unit={tourismKpis.receipts?.unit} sub="Sur la période" />
        <SummaryKpi icon={BedDouble} accent="text-emerald-600 border-t-4 border-t-emerald-400" label="Occupation moyenne" value={tourismKpis.roomOccupancy?.value || "—"} unit={tourismKpis.roomOccupancy?.unit} sub={lowOccupancy ? `${lowOccupancy.month} : ${formatNumber(lowOccupancy.rooms)} %` : "Destination globale"} />
      </div>

      <div className="rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm" data-testid="destination-market-benchmarks">
        <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.14em] text-lodge-deep">Repères marchés</p>
        <div className="grid grid-cols-1 divide-y divide-lodge-hairline sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
          {[
            [TrendingUp, snapshotKpis.volume, "Périmètre analysé"],
            [Target, snapshotKpis.growth, snapshotKpis.growth?.deltaLabel || "Dynamique pondérée"],
            [TrendingUp, snapshotKpis.markets, "Couverture de l’analyse"],
            [Trophy, snapshotKpis.top, snapshotKpis.top?.sub || "Premier marché"],
          ].map(([Icon, item, fallback], index) => (
            <div key={item?.key || index} className="flex items-center gap-3 px-3 py-4 first:pl-0 last:pr-0 sm:py-1">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-50 text-lodge-deep"><Icon size={19} /></span>
              <div className="min-w-0">
                <p className="text-xs text-lodge-ardoise">{item?.label}</p>
                <p className={`truncate font-heading text-2xl font-bold ${item?.positive === false ? "text-rose-600" : "text-lodge-graphite"}`}>{item?.value || "—"}{item?.unit ? ` ${item.unit}` : ""}</p>
                <p className="truncate text-[11px] text-slate-400">{item?.sub || fallback}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div id="destination-opportunities" className="grid grid-cols-1 gap-5 xl:grid-cols-3" data-testid="destination-opportunities">
        <article className="rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm xl:col-span-2">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Target size={21} className="text-amber-500" />
            <div>
              <h3 className="font-heading text-xl font-bold text-lodge-graphite">Vos 5 opportunités prioritaires</h3>
              <p className="text-xs text-slate-400">Où concentrer vos efforts en premier</p>
            </div>
            <span className="ml-auto rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-lodge-ardoise">{portfolio.length} détectée{portfolio.length > 1 ? "s" : ""} · {visibleOpportunities.length} affichée{visibleOpportunities.length > 1 ? "s" : ""}</span>
            {portfolio.length > 5 && <button type="button" onClick={() => setShowAll((value) => !value)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-lodge-deep hover:border-lodge-deep">{showAll ? "Réduire" : `Voir les ${portfolio.length}`} <ArrowRight size={13} /></button>}
          </div>
          {topFive.length ? (
            <ol className="divide-y divide-slate-100">
              {visibleOpportunities.map((opportunity, index) => {
                const priority = priorityMeta(opportunity);
                const code = marketCode(opportunity);
                return (
                  <li key={opportunity.id} className="grid grid-cols-[32px_minmax(0,1fr)_auto] items-center gap-3 py-3 sm:grid-cols-[32px_minmax(130px,0.7fr)_minmax(180px,1.5fr)_100px_34px]" data-testid={`destination-opportunity-${index}`}>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-50 text-xs font-bold text-lodge-deep">{index + 1}</span>
                    <p className="truncate text-sm font-semibold text-lodge-graphite"><span className="mr-2">{FLAG[code] || "🌐"}</span>{opportunity.market}</p>
                    <p className="hidden truncate text-xs text-lodge-ardoise sm:block">{opportunity.opportunity} · {opportunity.signal}</p>
                    <span className={`hidden rounded-full px-2.5 py-1 text-center text-[11px] font-semibold sm:inline ${priority.className}`}>{actionVerb(opportunity.type)}</span>
                    <button type="button" onClick={() => onOpenMarket(opportunity)} aria-label={`Voir ${opportunity.market}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-lodge-deep hover:border-lodge-deep"><ArrowRight size={14} /></button>
                  </li>
                );
              })}
            </ol>
          ) : <p className="rounded-xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">Les opportunités apparaîtront lorsque les données de marché seront disponibles.</p>}
        </article>

        <article className="rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm" data-testid="destination-moment">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600"><TrendingDown size={20} /></span>
            <div><h3 className="font-heading text-xl font-bold text-lodge-graphite">Le moment à saisir</h3><p className="text-xs text-slate-400">Saison basse · {lowOccupancy?.month || "à identifier"}</p></div>
          </div>
          <div className="mt-6 space-y-4">
            <div><div className="mb-1.5 flex justify-between text-xs"><span>Occupation moyenne</span><strong>{formatNumber(averageOccupancy)} %</strong></div><div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-violet-500" style={{ width: `${Math.min(100, averageOccupancy || 0)}%` }} /></div></div>
            <div><div className="mb-1.5 flex justify-between text-xs"><span>{lowOccupancy?.month || "Point bas"}</span><strong className="text-amber-600">{formatNumber(lowOccupancy?.rooms)} %</strong></div><div className="h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-amber-400" style={{ width: `${Math.min(100, Number(lowOccupancy?.rooms) || 0)}%` }} /></div></div>
          </div>
          <div className="mt-5 rounded-xl bg-rose-50 p-4 text-center"><p className="font-heading text-2xl font-bold text-lodge-graphite">{occupancyGap == null ? "—" : `−${formatNumber(occupancyGap)} points`}</p><p className="text-xs text-slate-500">d’occupation au point bas vs moyenne.</p></div>
          <div className="mt-3 flex items-start gap-3 rounded-xl bg-emerald-50 p-4"><Lightbulb size={18} className="mt-0.5 shrink-0 text-emerald-600" /><p className="text-sm text-lodge-deep">Activer une campagne avant {lowOccupancy?.month || "la basse saison"}, à ajuster aux capacités commercialisables.</p></div>
        </article>
      </div>

      <article className="rounded-2xl border border-lodge-hairline bg-white p-5 shadow-lodge-sm" data-testid="destination-action-plan">
        <div className="mb-4 flex items-center gap-3"><Target size={20} className="text-rose-500" /><div><h3 className="font-heading text-xl font-bold text-lodge-graphite">Votre plan d’action</h3><p className="text-xs text-slate-400">{actionPlan.length} décision{actionPlan.length > 1 ? "s" : ""} pour avancer</p></div></div>
        {actionPlan.length ? (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {actionPlan.map((opportunity, index) => (
              <div key={opportunity.id} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/50 p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white font-bold text-lodge-deep shadow-sm">{index + 1}</span>
                <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-lodge-graphite">{actionVerb(opportunity.type)} {opportunity.market}</p><p className="truncate text-xs text-slate-400">{suggestOppKpi(opportunity.type)} · <Confidence value={opportunity.confidence} /></p></div>
                <button type="button" onClick={() => immutableDemo ? onOpenMarket(opportunity) : onRunAction(opportunity)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-blue-600 hover:border-blue-300">Explorer <ArrowRight size={13} /></button>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-500">Aucun plan d’action n’est généré sans signal observé.</p>}
        {immutableDemo && <p className="mt-4 text-[11px] text-slate-400">Données de démonstration · recommandations à valider · aucune campagne n’est lancée depuis cet écran.</p>}
      </article>
    </section>
  );
}
